import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Calendar, User, Tag, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSocialMeta } from '../lib/useSocialMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const i18n = {
  en: {
    backToBlog: 'Back to Journal',
    notFound: 'Article not found',
    notFoundBody: 'The article you\'re looking for doesn\'t exist or was unpublished.',
    minRead: 'min read',
    loadError: 'Could not load this article.',
  },
};

function readingTime(text) {
  if (!text) return 1;
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

const BlogArticle = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const t = i18n[language] || i18n.en;
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/articles/${slug}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error('load failed');
        setArticle(await res.json());
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
  }, [slug]);

  // SEO + social cards: dynamic Open Graph / Twitter meta tags for the active article
  useSocialMeta(
    article
      ? {
          title: `${article.title} — Skopelos Journal`,
          description:
            article.seo_description ||
            article.excerpt ||
            article.title,
          image:
            article.hero_image_url ||
            'https://customer-assets.emergentagent.com/job_8e4dc794-01c5-4aa3-adec-b02ef8fc9fa7/artifacts/mki6abqp_dji_fly_20240529_065404_449_1716954893598_pano_optimized.jpg',
          imageWidth: '1200',
          imageHeight: '630',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          type: 'article',
        }
      : null
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md text-center">
          <h1 className="font-serif text-2xl text-slate-800 mb-3">{t.notFound}</h1>
          <p className="text-slate-600 mb-6">{t.notFoundBody}</p>
          <Button onClick={() => navigate('/blog')} className="bg-[#1e3a5f] hover:bg-[#15293f] text-white">
            {t.backToBlog}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
            data-testid="blog-article-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backToBlog}
          </Button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-article">
        <div className="mb-8">
          <div className="flex items-center flex-wrap gap-3 text-sm text-slate-500 mb-4">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(article.published_at || article.created_at)}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {article.author}
            </span>
            <span>·</span>
            <span>{readingTime(article.content)} {t.minRead}</span>
          </div>
          <h1 className="font-serif text-3xl md:text-5xl text-slate-800 leading-tight mb-4" data-testid="blog-article-title">
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="text-lg text-slate-600 leading-relaxed">{article.excerpt}</p>
          )}
        </div>

        {article.hero_image_url && (
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10 shadow-lg bg-slate-100">
            <img src={article.hero_image_url} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="article-content text-slate-700 leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
        </div>

        {article.tags && article.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs uppercase tracking-wide px-3 py-1 bg-slate-100 text-slate-600 rounded-full inline-flex items-center gap-1.5"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-12 bg-gradient-to-br from-[#fff7f1] to-[#fceee2] border border-[#d4845f]/30 rounded-2xl p-6 md:p-8 text-center">
          <h3 className="font-serif text-xl text-slate-800 mb-2">Interested in the property?</h3>
          <p className="text-sm text-slate-600 mb-4 max-w-md mx-auto">
            Browse the gallery, the location map and arrange a private viewing — directly with the owner.
          </p>
          <Link to="/">
            <Button className="bg-[#d4845f] hover:bg-[#c17552] text-white">
              View the property →
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogArticle;
